import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import GridLayout, {
  useContainerWidth,
  verticalCompactor,
  type Layout,
  type LayoutItem,
} from 'react-grid-layout'
// RGL's stylesheet also bundles the `.react-resizable-handle` styles.
import 'react-grid-layout/css/styles.css'
// Re-tints the red drop-placeholder to the brand primary. Must come after
// RGL's stylesheet so our override wins.
import './grid-workspace.css'
import type { ComponentType, GridCell, WindowConfig } from '@/types/schemas/workspace'
import { cn } from '@/lib/utils'
import { useLayoutStore } from '../layout-store/hooks'
import { reconcileGridCells, DEFAULT_GRID_COLS } from '../grid/mosaic-to-grid'
import { GridPanelFrame } from './grid-panel-frame'
import { useDrillSourcePanelIds } from '@/features/history/hooks/use-drill-source-panel-ids'

const ROW_HEIGHT = 20
// Gap between tiles. 6px matches the mosaic, where two adjacent `.mosaic-tile`s
// each carry a 3px margin, so the visible tile-to-tile gap there is 6px.
const MARGIN: [number, number] = [6, 6]
// Inner padding around the whole grid — kept smaller than the inter-tile gap.
// The -3px wrapper shift (below) cancels it on the top/left so panels touch the
// header + nav rail, leaving ~6px on the right/bottom: the same outer-vs-inner
// spacing the mosaic root gets from `.mosaic-root { top/left: -3px; right/bottom: 3px }`.
const CONTAINER_PADDING: [number, number] = [3, 3]

/**
 * Panel types with an internal scroll that would hijack the page wheel — these
 * get the click-to-interact shield. Everything else (charts, scorecards,
 * time-series, intraday, etc.) stays always-interactive. Allow-list on purpose:
 * an unknown type defaults to no shield (the less-intrusive behavior).
 */
const CLICK_TO_INTERACT_TYPES: ReadonlySet<ComponentType> = new Set([
  'history-chart',
  'backtest-trade-chart',
  'history-multi-chart',
  'multi-chart',
  'history-records',
  'history-news',
  'history-calendar',
  'sector-heatmap',
  'history-heatmap',
  'history-time-distribution',
  'tags-explorer',
  'history-stats',
  'history-comparison',
  'notebook',
])

interface GridWorkspaceProps {
  /** Render a panel's toolbar (the real PanelHeader). Supplied by the mosaic
   *  host so grid cells reuse the exact same header wiring as mosaic mode. */
  renderHeader: (panelId: string, config: WindowConfig) => ReactNode
  /** Render a panel's body (the registry component + its handlers). */
  renderBody: (panelId: string, config: WindowConfig) => ReactNode
  /**
   * Every cell takes the height its content needs, measured live, and no
   * cell drags or resizes: a read-through page of tiles sized to their data.
   * The stored heights are only the first paint before the first measure,
   * and a panel whose content grows (a finer interval, more rows) grows its
   * cell with it. See PresetLayout.gridFitContent.
   */
  fitContent?: boolean
  /**
   * Cells keep the heights in the grid layout and never drag or resize, but
   * are not measured to their content. See PresetLayout.gridFixed.
   */
  fixed?: boolean
  /**
   * Cover a panel with an inner scroll (CLICK_TO_INTERACT_TYPES) until it is
   * pressed, so the wheel scrolls the page past it. On by default; a host
   * whose panels take the first click (the History page) turns it off.
   */
  clickToInteract?: boolean
}

/** Rows a cell needs to show `px` of content: a cell of h rows is
 *  h * ROW_HEIGHT + (h - 1) * MARGIN tall. */
function rowsForPx(px: number): number {
  return Math.max(1, Math.ceil((px + MARGIN[1]) / (ROW_HEIGHT + MARGIN[1])))
}

/** Nearest scrollable ancestor (overflow-y auto/scroll with actual overflow). */
function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  for (let n = el?.parentElement ?? null; n; n = n.parentElement) {
    const oy = getComputedStyle(n).overflowY
    if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) return n
  }
  return null
}

/** Keep only the persisted grid fields — RGL adds `moved`/`static` at runtime. */
function normalizeCell(item: LayoutItem): GridCell {
  const cell: GridCell = { i: item.i, x: item.x, y: item.y, w: item.w, h: item.h }
  if (item.minW != null) cell.minW = item.minW
  if (item.minH != null) cell.minH = item.minH
  return cell
}

/**
 * Vertically-scrollable, customizable renderer — the 'grid' layoutMode. Reuses
 * the same panels, prop contract, and layout store as the mosaic renderer; only
 * the container differs (react-grid-layout in a scroll area instead of
 * absolute-positioned mosaic tiles).
 *
 * react-grid-layout v2 API: `useContainerWidth` supplies the measured width
 * (required — no v1 `WidthProvider` HOC), behaviour is grouped into
 * `gridConfig`/`dragConfig`, and container padding MUST go through
 * `gridConfig.containerPadding` (RGL's width math is padding-unaware, so CSS
 * padding would push cells past the right edge). Cells are reconciled against
 * `windowConfigs` each render so a newly-added panel still renders — see
 * reconcileGridCells.
 */
export function GridWorkspace({
  renderHeader,
  renderBody,
  fitContent = false,
  fixed = false,
  clickToInteract = true,
}: GridWorkspaceProps) {
  const gridLayout = useLayoutStore((s) => s.gridLayout)
  const windowConfigs = useLayoutStore((s) => s.windowConfigs)
  const setGridLayout = useLayoutStore((s) => s.setGridLayout)
  // Panels a header filter chip was drilled from (History page); empty elsewhere.
  const drillSourceIds = useDrillSourcePanelIds()
  // measureBeforeMount: defer the first paint until the real width is known, so
  // cells never render against the 1280px default and overflow the container.
  const { width, containerRef, mounted } = useContainerWidth({ measureBeforeMount: true })

  // ─── Click-to-interact ───
  // One panel is "active" (interactive) at a time; the rest show a shield that
  // lets the wheel scroll the page. A pointerdown inside a panel activates it;
  // anywhere else releases. Only in scroll mode (mosaic tiles fill the viewport,
  // so there's no page-scroll conflict).
  const [activePanelId, setActivePanel] = useState<string | null>(null)
  useEffect(() => {
    const resolvePanel = (target: EventTarget | null): string | null | undefined => {
      const el = target as HTMLElement | null
      if (!el?.closest) return null
      // Clicks inside portalled overlays (Radix dialogs/sheets, dropdowns,
      // popovers) belong to the panel that opened them — deactivating here
      // would re-shield the panel behind its own open settings sheet. Leave
      // activation unchanged (undefined = no-op).
      if (el.closest('[role="dialog"], [data-radix-popper-content-wrapper]')) return undefined
      return el.closest('[data-grid-panel]')?.getAttribute('data-grid-panel') ?? null
    }
    const onPointerDown = (e: PointerEvent) => {
      const id = resolvePanel(e.target)
      if (id !== undefined) setActivePanel(id)
    }
    // Keyboard path: tabbing into a panel's content activates it (lifts the
    // click-to-interact shield) without requiring a pointer.
    const onFocusIn = (e: FocusEvent) => {
      const id = resolvePanel(e.target)
      if (id != null) setActivePanel(id)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('focusin', onFocusIn)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('focusin', onFocusIn)
    }
  }, [])

  const panelIds = useMemo(() => Object.keys(windowConfigs), [windowConfigs])
  const cells = useMemo(() => reconcileGridCells(gridLayout, panelIds), [gridLayout, panelIds])

  // ─── Fit to content ───
  // Each frame reports its natural height; a cell whose row count differs is
  // rewritten and the stack below it re-flowed. Batched to one store write per
  // frame so several tiles measuring at once do not thrash the layout.
  const measured = useRef(new Map<string, number>())
  const fitRaf = useRef<number | null>(null)
  const cellsRef = useRef(cells)
  cellsRef.current = cells
  const applyFit = useCallback(() => {
    fitRaf.current = null
    const current = cellsRef.current
    let changed = false
    const next = current.map((cell) => {
      const px = measured.current.get(cell.i)
      if (px == null) return cell
      const h = rowsForPx(px)
      if (h === cell.h) return cell
      changed = true
      return { ...cell, h }
    })
    if (!changed) return
    // Re-stack in reading order so a taller cell pushes the ones below it
    // down instead of overlapping them until RGL's compactor catches up.
    const ordered = [...next].sort((a, b) => a.y - b.y || a.x - b.x)
    let y = 0
    for (const cell of ordered) {
      cell.y = y
      y += cell.h
    }
    setGridLayout(ordered)
  }, [setGridLayout])
  const reportHeight = useCallback(
    (panelId: string, px: number) => {
      if (measured.current.get(panelId) === px) return
      measured.current.set(panelId, px)
      if (fitRaf.current == null) fitRaf.current = requestAnimationFrame(applyFit)
    },
    [applyFit],
  )
  const heightReporters = useMemo(() => {
    const map = new Map<string, (px: number) => void>()
    for (const id of panelIds) map.set(id, (px) => reportHeight(id, px))
    return map
  }, [panelIds, reportHeight])
  useEffect(
    () => () => {
      if (fitRaf.current != null) cancelAnimationFrame(fitRaf.current)
    },
    [],
  )

  // Persist only when the user finishes a drag/resize — not on every tick, and
  // not on the mount-time compaction that onLayoutChange would fire.
  const persist = useCallback(
    (layout: Layout) => setGridLayout(layout.map(normalizeCell)),
    [setGridLayout],
  )

  // ─── Edge auto-scroll during drag/resize ───
  // The drag core only recalculates on mousemove, and browsers don't
  // auto-scroll a custom overflow container during a pointer drag — so
  // resizing/dragging past the viewport bottom would just stop at the edge.
  // While an operation is active, a rAF loop scrolls the container when the
  // pointer sits near its top/bottom edge, then dispatches a synthetic
  // mousemove at the (stationary) pointer position so the drag core recomputes
  // against the grid's shifted bounding rect and the panel keeps growing.
  const autoScroll = useRef<{ raf: number | null; x: number; y: number }>({
    raf: null,
    x: 0,
    y: 0,
  })

  const trackPointer = useCallback((e: Event | undefined) => {
    const me = e as MouseEvent | undefined
    if (me && typeof me.clientX === 'number' && typeof me.clientY === 'number') {
      autoScroll.current.x = me.clientX
      autoScroll.current.y = me.clientY
    }
  }, [])

  const stopAutoScroll = useCallback(() => {
    if (autoScroll.current.raf != null) cancelAnimationFrame(autoScroll.current.raf)
    autoScroll.current.raf = null
  }, [])

  const startAutoScroll = useCallback(
    (e: Event | undefined) => {
      trackPointer(e)
      const EDGE = 48
      const MAX_SPEED = 24
      const tick = () => {
        const scroller = findScrollParent(containerRef.current)
        if (scroller) {
          const rect = scroller.getBoundingClientRect()
          const { x, y } = autoScroll.current
          let delta = 0
          if (y > rect.bottom - EDGE)
            delta = Math.min(MAX_SPEED, (y - (rect.bottom - EDGE)) / 2 + 4)
          else if (y < rect.top + EDGE) delta = -Math.min(MAX_SPEED, (rect.top + EDGE - y) / 2 + 4)
          if (delta !== 0) {
            const before = scroller.scrollTop
            scroller.scrollTop = before + delta
            if (scroller.scrollTop !== before) {
              document.dispatchEvent(
                new MouseEvent('mousemove', { bubbles: true, clientX: x, clientY: y }),
              )
            }
          }
        }
        autoScroll.current.raf = requestAnimationFrame(tick)
      }
      stopAutoScroll()
      autoScroll.current.raf = requestAnimationFrame(tick)
    },
    [trackPointer, stopAutoScroll, containerRef],
  )

  // Cancel the loop if the component unmounts mid-drag.
  useEffect(() => stopAutoScroll, [stopAutoScroll])

  const handleOperationStop = useCallback(
    (layout: Layout) => {
      stopAutoScroll()
      persist(layout)
    },
    [stopAutoScroll, persist],
  )

  return (
    // Height must be auto (NOT h-full): RGL's autoSize grows
    // `.react-grid-layout` to fit the rows, and this wrapper has to grow with
    // it so the parent overflow-y-auto container actually scrolls. Pinning
    // h-full caps content at the viewport height → no growth, no scroll.
    //
    // The -3px top/left shift cancels the top/left `containerPadding` so panels
    // TOUCH the page header and the secondary-nav rail (nav-facing edges),
    // while right/bottom keep their padding for breathing room — the same
    // outer-vs-inner spacing the mosaic gets from `.mosaic-root { top/left: -3px }`.
    // A relative offset (not a negative margin) so RGL's width measurement is
    // unaffected. Not a transform/translate either: a transformed ancestor
    // becomes the containing block for the focus gallery's `position: fixed`
    // promotion (features/history/focus/promote-panel.ts) and traps the panel
    // off-screen.
    <div
      ref={containerRef}
      className={cn(
        'edge-grid-shell relative -top-[3px] -left-[3px] w-full',
        fitContent && 'edge-grid-fit',
        fixed && 'edge-grid-fixed',
      )}
    >
      {mounted && (
        <GridLayout
          className="edge-grid-workspace"
          width={width}
          layout={cells as Layout}
          gridConfig={{
            cols: DEFAULT_GRID_COLS,
            rowHeight: ROW_HEIGHT,
            margin: MARGIN,
            containerPadding: CONTAINER_PADDING,
          }}
          // Whole header row drags; interactive controls in it are excluded so
          // buttons/menus still click.
          dragConfig={{
            enabled: !fitContent && !fixed,
            handle: '.grid-panel-header',
            cancel:
              'button, a, input, select, textarea, [role="button"], [role="combobox"], [role="menuitem"], [contenteditable="true"]',
          }}
          // Resize from both bottom corners.
          resizeConfig={{ enabled: !fitContent && !fixed, handles: ['se', 'sw'] }}
          compactor={verticalCompactor}
          autoSize
          onDragStart={(_l, _o, _n, _p, e) => startAutoScroll(e)}
          onDrag={(_l, _o, _n, _p, e) => trackPointer(e)}
          onDragStop={handleOperationStop}
          onResizeStart={(_l, _o, _n, _p, e) => startAutoScroll(e)}
          onResize={(_l, _o, _n, _p, e) => trackPointer(e)}
          onResizeStop={handleOperationStop}
        >
          {cells.map((cell) => {
            const cfg = windowConfigs[cell.i]
            if (!cfg) return null
            return (
              <div key={cell.i}>
                <GridPanelFrame
                  panelId={cell.i}
                  // A fitted panel has no inner scroll to hijack the wheel.
                  shielded={
                    clickToInteract && !fitContent && CLICK_TO_INTERACT_TYPES.has(cfg.componentType)
                  }
                  active={activePanelId === cell.i}
                  drillSource={drillSourceIds.has(cell.i)}
                  header={renderHeader(cell.i, cfg)}
                  onContentHeight={fitContent ? heightReporters.get(cell.i) : undefined}
                >
                  {renderBody(cell.i, cfg)}
                </GridPanelFrame>
              </div>
            )
          })}
        </GridLayout>
      )}
    </div>
  )
}
